import { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

function SimpleTooltip({ active, payload, label, labelFormatter }) {
  if (!active || !payload?.length) return null;
  const title = labelFormatter ? labelFormatter(label, payload) : label;

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        px: 1.5,
        py: 1,
        boxShadow: 2,
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      {payload.length === 1 && (
        <Typography variant="body2" sx={{ color: payload[0].color }}>
          {payload[0].name} : {payload[0].value}
        </Typography>
      )}
    </Box>
  );
}

function TipDetailsPanel({ tip, labelFormatter }) {
  if (!tip?.payload?.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Pasá el mouse sobre un punto para ver el detalle.
      </Typography>
    );
  }

  const title = labelFormatter
    ? labelFormatter(tip.label, tip.payload)
    : tip.label;

  return (
    <Box
      sx={{
        mt: 1,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
        maxHeight: 220,
        overflowY: "auto",
        bgcolor: "background.paper",
      }}
    >
      {title != null && title !== "" && (
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {title}
        </Typography>
      )}
      {tip.payload.map((entry) => (
        <Typography
          key={entry.dataKey}
          variant="body2"
          sx={{ color: entry.color, lineHeight: 1.6 }}
        >
          {entry.name} : {entry.value}
        </Typography>
      ))}
    </Box>
  );
}

export default function StatLineChart({
  data,
  xKey,
  stroke = "#1976d2",
  labelFormatter,
  height = 320,
  dotRadius = 4,
  lines,
}) {
  const resolvedLines = lines ?? [
    { dataKey: "total", stroke, name: "Usuarios" },
  ];
  const isMultiSeries = resolvedLines.length > 1;
  const [tip, setTip] = useState(null);

  const handleChartMouseMove = (state) => {
    if (state?.isTooltipActive && state.activePayload?.length) {
      setTip({
        payload: state.activePayload,
        label: state.activeLabel,
      });
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
            onMouseMove={isMultiSeries ? handleChartMouseMove : undefined}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} />
            <YAxis allowDecimals={false} />
            <ReTooltip
              content={
                <SimpleTooltip labelFormatter={labelFormatter} />
              }
              allowEscapeViewBox={{ x: true, y: true }}
              isAnimationActive={false}
            />
            {isMultiSeries && <Legend />}
            {resolvedLines.map((line) => (
              <Line
                key={line.dataKey}
                type="monotone"
                dataKey={line.dataKey}
                name={line.name ?? line.dataKey}
                stroke={line.stroke}
                strokeWidth={2}
                dot={{ r: dotRadius }}
                activeDot={{ r: dotRadius + 2 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      {isMultiSeries && (
        <TipDetailsPanel tip={tip} labelFormatter={labelFormatter} />
      )}
    </Box>
  );
}
